<template>
  <b-modal
    :id="`mc-modal-complete-v2-${activity.id}`"
    ref="mc-modal-complete-v2"
    class="mc-modal"
    :title="title"
    size="xl"
    centered
    scrollable
    hide-header-close
    no-close-on-esc
    no-close-on-backdrop
    @show="handleModalShow"
  >
    <div v-if="!isComplete" class="card-body p-2">
      <p class="label__do-number ms-1 mb-1">
        {{ `Bukti ${isPickUp ? 'Pengambilan' : 'Pengiriman'}` }}
      </p>

      <FormulateForm ref="formRef" v-model="formValues" class="mc-form" :errors="formErrors">
        <b-row>
          <b-col md="6">
            <FormulateInput
              type="text"
              validation="required"
              name="contact_person_name"
              :help="true"
              :label="isPickUp ? 'Nama Pengirim' : 'Nama Penerima'"
              :placeholder="isPickUp ? 'Masukkan nama pengirim' : 'Masukkan nama penerima'"
              :disabled="isLoading"
            />
          </b-col>
          <b-col md="6">
            <FormulateInput
              type="date-picker"
              validation="bail|required"
              format="DD MMM YYYY HH:mm"
              datepickerType="datetime"
              valueType="YYYY-MM-DDTHH:mm:ss.SSSZ"
              confirmText="Terapkan"
              name="completed_on"
              :help="true"
              :confirm="true"
              :showSecond="false"
              :label="isPickUp ? 'Tanggal Pengambilan' : 'Tanggal Pengiriman'"
              :placeholder="isPickUp ? 'Pilih tanggal pengambilan' : 'Pilih tanggal pengiriman'"
              :disabled="isLoading"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <AttachmentField
              :activity="activity"
              :fleetTaskId="fleetTaskId"
              :pointId="pointId"
              :attachments="attachments"
              :isLoading="isLoading"
              @refetch-attachments="fetchAttachments"
              @update:isLoading="isLoading = $event"
            />
          </b-col>

          <b-col md="6">
            <FormulateInput
              type="textarea"
              name="note"
              label="Catatan"
              class="note-textarea"
              placeholder="Masukkan catatan"
              :disabled="isLoading"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <FormulateInput
              id="customer_notes"
              ref="customer_notes-ref"
              v-model="customerNotes"
              name="customer_notes"
              class="mt-2"
              :label="$t('global.field.additionalNote')"
              :placeholder="$t('fleetTask.placeholder.additionalNote')"
              trackBy="note"
              displayedBy="note"
              :multiple="true"
              type="select-resourceful-v3"
              :optionsGetter="resourcefulQuery.customerNotes"
              :optionLabelFn="({ item }) => item.note"
              :fullObject="fullObject.customerNotes"
              selectedLabel="note"
              :hasClose="false"
              :visibleTagCount="3"
              @update:fullObject="($event) => (fullObject.customerNotes = $event)"
            />
          </b-col>
        </b-row>
      </FormulateForm>
    </div>

    <DeliveryLoad
      v-if="
        hasAccessAll('delivery-load-adjustment:read', 'delivery-load-adjustment:update') &&
        enabledAdjustment
      "
      ref="deliveryLoadComponent"
      :fleetTaskId="fleetTaskId"
      :customerNotes="customerNotes"
      :activity="activity"
      :isLoading="isLoading"
      :isPickUp="isPickUp"
      :isComplete="isComplete"
      :isCompletedPickUp="isCompletedPickUp"
      :isCompletedDrop="isCompletedDrop"
      :isAdjustedActivity="deliveryOrderDetails.is_load_adjusted"
      @update:adjusted-loads="handleAdjustedLoads"
      @update:has-errors="hasDeliveryLoadErrors = $event"
    />

    <template #modal-footer>
      <div class="d-flex mx-0">
        <McButton
          variant="secondary"
          class="me-2"
          :text="$t('global.cancel')"
          @click="handleModalCancel"
        />
        <McButton
          v-if="!(isCompletedDrop && isCompletedPickUp && isPickUp)"
          :disabled="hasDeliveryLoadErrors"
          variant="primary"
          :text="isComplete ? 'Simpan Perubahan' : 'Ya, selesaikan'"
          @click="isComplete ? handleUpdate() : handleComplete()"
        />
      </div>
    </template>
  </b-modal>
</template>

<script>
import McButton from '@/components/McButton'
import * as FleetTaskActivity from '@/services/rest-api/lib/fleet-task/activity'
import * as DeliveryNote from '@/services/rest-api/lib/customer/delivery-note'
import AttachmentField from './AttachmentField'
import { EventBus } from '@/router/views/fleet-task/lib/event-bus'
import { mapState, mapGetters } from 'vuex'
import { MixPanel } from '@/mixin/mixpanel'
import { Helper } from '@/mixin/helper'

import getRelatedActivity from '../lib/get-related-activity'
import { getValidateQuantityButtonState } from '../lib/helpers'
import toastErrors from '../lib/constants/toast-errors'
import apiErrorMappings from '../lib/constants/api-error-mappings'
import { errorCodeInputMap } from '../lib/constants/error-code-mappings'
import DeliveryLoad from './DeliveryLoad'

import moment from 'moment'
moment.locale('id')

export default {
  components: {
    McButton,
    AttachmentField,
    DeliveryLoad,
  },
  mixins: [MixPanel, Helper],
  props: {
    activity: {
      type: Object,
      required: true,
    },
    fleetTaskId: {
      type: String,
      required: true,
    },
    pointId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,

      formValues: {},
      formErrors: {},
      invalidFormValues: {},

      attachments: [],
      adjustedDeliveryLoads: [],
      customerNotes: [],
      fullObject: {
        customerNotes: [],
      },
      hasDeliveryLoadErrors: false,
      convertedDeliveries: [],
    }
  },
  computed: {
    ...mapState('fleetTask', [
      'sequence',
      'fleetTaskDetail',
      'fleetTaskStatus',
      'isLoadingGlobal',
      'selectedDos',
    ]),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isSamator() {
      const data = this.getFeatureFlagValue('customFeature.samator')
      return Array.isArray(data?.company_id) && data.company_id.includes(this.getCompanyId())
    },
    validateQuantityButtonState() {
      return getValidateQuantityButtonState({
        isSamator: this.isSamator,
        fleetTaskStatus: this.fleetTaskStatus,
        fleetTask: this.fleetTaskDetail,
        sequence: this.sequence,
        isLoading: this.isLoadingGlobal,
      })
    },
    validateQuantityButtonVisible() {
      return this.validateQuantityButtonState.visible
    },
    validateQuantityButtonDisabled() {
      return this.validateQuantityButtonState.disabled
    },
    title() {
      if (
        (!this.isCompletedPickUp && !this.isCompletedDrop) ||
        (this.isCompletedPickUp && !this.isCompletedDrop && !this.isPickUp)
      ) {
        return `Selesaikan ${this.isPickUp ? 'Pengambilan' : 'Pengiriman'} #${
          this.activity?.delivery_order?.number
        }`
      } else if (!(this.isCompletedDrop && this.isCompletedPickUp && this.isPickUp)) {
        return `Ubah Kuantitas ${this.isPickUp ? 'Pengambilan' : 'Pengiriman'} #${
          this.activity?.delivery_order?.number
        }`
      }
      return `Lihat Muatan ${this.isPickUp ? 'Pengambilan' : 'Pengiriman'} #${
        this.activity?.delivery_order?.number
      }`
    },
    deliveryOrderDetails() {
      const targetId = this.activity?.delivery_order?.id
      return this.selectedDos.find((doNumber) => doNumber.id === targetId) || {}
    },
    isPickUp() {
      return this.activity.type == 'PICKUP'
    },
    isComplete() {
      return this.activity?.status?.raw_type == 'COMPLETED'
    },
    isCompletedPickUp() {
      const targetId = this.activity?.delivery_order?.id
      const defaultPoints = this.sequence.filter((point) => point.type === 'DEFAULT')
      const found = defaultPoints.some((point) => {
        return point.activities.some(
          (activity) =>
            activity.type === 'PICKUP' &&
            activity.delivery_order.id === targetId &&
            activity.status.raw_type === 'COMPLETED'
        )
      })

      return found
    },
    isCompletedDrop() {
      const targetId = this.activity?.delivery_order?.id
      const defaultPoints = this.sequence.filter((point) => point.type === 'DEFAULT')
      const found = defaultPoints.some((point) => {
        return point.activities.some(
          (activity) =>
            activity.type === 'DROP' &&
            activity.delivery_order.id === targetId &&
            activity.status.raw_type === 'COMPLETED'
        )
      })
      return found
    },
    enabledAdjustment() {
      const deliveryOrderDetail = this.deliveryOrderDetails
      const deliveryOrderDetailOrigin = deliveryOrderDetail?.origin_address?.id
      const deliveryOrderDetailDestination = deliveryOrderDetail?.destination_address?.id

      const sequenceDetail = this.sequence.find((point) => point.id === this.pointId)
      const sequenceDetailAddress = sequenceDetail?.address?.id

      const hasLoad = deliveryOrderDetail?.load_count > 0 ? true : false

      if (deliveryOrderDetail.is_from_vendor_request) {
        return false
      } else if (deliveryOrderDetail?.shipment_type === 'DIRECT') {
        if (!hasLoad) return false
        return true
      } else if (deliveryOrderDetail?.shipment_type === 'TRANSIT') {
        if (this.activity.type === 'PICKUP') {
          if (deliveryOrderDetailOrigin === sequenceDetailAddress) {
            if (!hasLoad) return false
            return true
          }
        } else if (this.activity.type === 'DROP') {
          if (deliveryOrderDetailDestination === sequenceDetailAddress) {
            if (!hasLoad) return false
            return true
          }
        }
        return false
      }
      return false
    },
    hasChanged() {
      if (this.isLoading) return false

      let currentFormValues = this.formValues

      const [_, hasChanged] = this.filterUnchangedData(currentFormValues, {})

      const hasAdjustedLoads = this.adjustedDeliveryLoads.length > 0

      return hasChanged || hasAdjustedLoads
    },
    customDateValidationMessage() {
      return this.isPickUp
        ? 'Waktu Pengambilan tidak boleh kurang dari Waktu Keberangkatan'
        : 'Waktu Pengiriman tidak boleh kurang dari Waktu Pengambilan'
    },
    relatedActivity() {
      return getRelatedActivity(this.activity, this.sequence)
    },
    invalidFieldKeys() {
      return Object.keys(this.invalidFormValues)
    },
    resourcefulQuery() {
      const customerId = this.deliveryOrderDetails?.customer?.id
      return {
        customerNotes: async (opts = {}) => {
          if (!customerId) {
            return {
              data: { paginated_result: [] },
              metadata: { total_page: 1, total_count: 0, page: 1 },
            }
          }

          const getter = DeliveryNote.get(customerId)
          return await getter(opts)
        },
      }
    },
  },
  watch: {
    async formValues() {
      if (this.invalidFieldKeys.length === 0) return

      this.invalidFieldKeys.forEach((fieldErrorKey) => {
        if (this.invalidFormValues[fieldErrorKey] != this.formValues[fieldErrorKey]) {
          // delete the field from the state to show errors
          this.$delete(this.formErrors, fieldErrorKey)
          this.$delete(this.invalidFormValues, fieldErrorKey)
        }
      })

      // empty the invalidFormValues if all error key is deleted
      if (this.invalidFieldKeys.length === 0) {
        this.invalidFormValues = {}
      }
    },
  },
  methods: {
    HandleCompleteActivity() {
      if (!this.isPickUp && !this.relatedActivity.completed_on) {
        this.$mcToast.error(toastErrors['invalid/pickup-must-be-completed'])
        return
      }
      if (this.validateQuantityButtonVisible && this.validateQuantityButtonDisabled) {
        this.$mcToast.error(toastErrors['invalid/validation-started'])
        return
      }

      this.$bvModal.show(`mc-modal-complete-v2-${this.activity.id}`)
    },
    customDateValidation(context) {
      const dt = moment(context.value)

      if (this.isPickUp) {
        return dt.isSameOrAfter(this.fleetTaskDetail.actual_departure_on)
      } else {
        return dt.isSameOrAfter(this.relatedActivity.completed_on)
      }
    },
    handleAdjustedLoads(loads) {
      this.adjustedDeliveryLoads = loads
    },
    handleConverted(delivery_id, is_converted) {
      if (is_converted && !this.convertedDeliveries.includes(delivery_id)) {
        this.convertedDeliveries.push(delivery_id)
      } else if (!is_converted) {
        const index = this.convertedDeliveries.indexOf(delivery_id)
        if (index > -1) {
          this.convertedDeliveries.splice(index, 1)
        }
      }
    },
    async handleComplete() {
      this.isLoading = true

      if (this.$refs.formRef.hasErrors) {
        this.$refs.formRef.showErrors()
        this.$mcToast.error(
          `Gagal menyelesaikan ${
            this.isPickUp ? 'pengambilan' : 'pengiriman'
          }. Mohon periksa kembali data Anda.`
        )
        this.isLoading = false
        return
      }

      const data = {
        ...this.formValues,
        type: 'admin',
      }

      if (this.adjustedDeliveryLoads.length > 0) {
        data.adjusted_delivery_loads = this.adjustedDeliveryLoads
      }

      if (this.customerNotes.length > 0) {
        data.customer_notes = this.customerNotes
      }

      const res = await FleetTaskActivity.complete(
        this.fleetTaskId,
        this.pointId,
        this.activity.id,
        data
      )

      if (res.errors && res.status == 400 && res.message.includes('KMDI Anji API response error')) {
        this.$mcToast.error(res.message)
        this.isLoading = false
        return
      }

      const issues = res.errors_v2?.issues

      if (Array.isArray(issues) && issues.length > 0) {
        const targetCodes = [
          'FLEET_TASK.FLEET_TASK_ACTIVITIES_NOT_COMPLETED',
          'FLEET_TASK.DELIVERY_ORDER_INVALID_STATUS',
          'FLEET_TASK.FLEET_TASK_ACTIVITIES_PARENT_NOT_COMPLETED',
        ]

        const hasRelevantIssue = issues.some((issue) => targetCodes.includes(issue.code))

        if (hasRelevantIssue) {
          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: `Pengiriman <strong>${this.activity.delivery_order?.number}</strong> sedang dalam proses transit di penugasan lain`,
                },
              ],
            },
          })
          this.isLoading = false
          return
        } else {
          const issueCode = issues[0].code
          const fieldKey = issues[0].path[0]
          const findErrorMapping = errorCodeInputMap[issueCode]
          if (findErrorMapping) {
            this.$set(this.formErrors, fieldKey, findErrorMapping)
            this.isLoading = false
            return
          } else {
            this.assignErrors(res.errors)
            this.isLoading = false
            return
          }
        }
      }

      if (res.errors && res.status == 400) {
        this.assignErrors(res.errors)
        this.isLoading = false
        return
      }

      this.isLoading = false
      this.$bvModal.hide(`mc-modal-complete-v2-${this.activity.id}`)

      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              html: `Pengiriman <strong>${this.activity.delivery_order?.number}</strong> berhasil ${
                this.isPickUp ? 'diterima' : 'terkirim'
              }!`,
            },
          ],
        },
      })

      if (this.adjustedDeliveryLoads.length > 0) {
        this.track('Delivery Load Adjustment on Transition', {
          'Fleet Order Number': this.fleetTaskDetail.number,
          'Activity Name': this.isPickUp ? 'Ambil' : 'Kirim',
        })
      }

      this.adjustedDeliveryLoads = []
      EventBus.$emit('refetch-sequence')
    },
    async fetchAttachments() {
      this.isLoading = true
      const res = await FleetTaskActivity.getAttachments(
        this.fleetTaskId,
        this.pointId,
        this.activity.id
      )

      if (res?.errors) {
        this.$mcToast(toastErrors['attachment/failed-to-get'])
        return
      }

      this.isLoading = false
      this.attachments = res.data
    },
    normalizeCustomerNotes(rawNotes) {
      const values = []
      const objects = []
      const normalized = Array.isArray(rawNotes)
        ? rawNotes
        : rawNotes === null || rawNotes === undefined || rawNotes === ''
        ? []
        : [rawNotes]

      normalized.forEach((note) => {
        if (note && typeof note === 'object') {
          const value =
            note.id ||
            note.uuid ||
            note.third_party_id ||
            note.customer_note_uuid ||
            note.value ||
            ''

          if (value) {
            values.push(value)
          }
          objects.push(note)
          return
        }

        if (note) {
          values.push(note)
        }
      })

      return {
        values,
        objects,
      }
    },
    async syncCustomerNotesFromCompletedActivity() {
      if (!this.isComplete || !this.activity?.delivery_order?.id) return

      try {
        const response = await FleetTaskActivity.getActivity(
          this.fleetTaskId,
          this.activity.delivery_order.id
        )

        if (!Array.isArray(response?.data)) return

        const activityType = this.isPickUp ? 'PICKUP' : 'DROP'
        const detail = response.data.find((item) => item.type === activityType)
        const normalizedNotes = this.normalizeCustomerNotes(detail?.customer_notes)

        this.customerNotes = normalizedNotes.values
        this.fullObject = {
          customerNotes: normalizedNotes.objects,
        }
      } catch (error) {
        this.customerNotes = []
        this.fullObject = {
          customerNotes: [],
        }
      }
    },
    async handleModalShow() {
      this.fetchAttachments()
      this.formErrors = {}
      this.customerNotes = []
      this.fullObject = {
        customerNotes: [],
      }
      this.hasDeliveryLoadErrors = false
      this.convertedDeliveries = []

      await this.syncCustomerNotesFromCompletedActivity()
    },
    async handleModalCancel() {
      this.attachments = []
      await FleetTaskActivity.deleteAllAttachments(this.fleetTaskId, this.pointId, this.activity.id)
      this.$bvModal.hide(`mc-modal-complete-v2-${this.activity.id}`)
    },
    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}

      keys.forEach((key) => {
        // if both value is falsy
        if (!oldValue[key] && !newValue[key]) return

        // if oldValue has same value with newValue
        if (oldValue[key] == newValue[key]) return

        // if the oldValue is 'Invalid date' and newValue is null
        if (oldValue[key] === 'Invalid date' && newValue[key] === null) return

        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    resetErrors() {
      this.invalidFormValues = {}
      this.formErrors = {}
    },
    assignErrors(errors) {
      this.resetErrors()

      const fieldErrorKeys = Object.keys(errors)

      fieldErrorKeys.forEach((fieldKey) => {
        this.$set(this.formErrors, fieldKey, apiErrorMappings[fieldKey][errors[fieldKey][0]])
        this.$set(this.invalidFormValues, fieldKey, this.formValues[fieldKey])
      })
    },
    async handleUpdate() {
      this.isLoading = true
      const deliveryLoadComponent = this.$refs['deliveryLoadComponent']

      if (!deliveryLoadComponent) {
        this.isLoading = false
        return
      }

      const data = this.adjustedDeliveryLoads

      const res = await FleetTaskActivity.adjustLoad(
        this.fleetTaskId,
        this.pointId,
        this.activity.id,
        data
      )

      if (res.errors_v2) {
        const hasErrorReasonNull = res.errors_v2.issues.some(
          (issue) =>
            issue.code ===
            'FLEET_TASK.FLEET_TASK_DELIVERY_LOAD_ADJUSTMENT_INVALID_REASON_CANNOT_BE_NULL'
        )

        if (hasErrorReasonNull) {
          const loadsWithMissingReasons = this.adjustedDeliveryLoads.filter((load) => !load.reason)
          deliveryLoadComponent.highlightErrorFields(loadsWithMissingReasons)

          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: `Gagal Menyimpan. Terdapat <strong>Alasan Perubahan</strong> yang belum diisi`,
                },
              ],
            },
          })
          this.isLoading = false
          return
        }

        this.isLoading = false
        return
      }

      this.isLoading = false
      this.$bvModal.hide(`mc-modal-complete-v2-${this.activity.id}`)
      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              html: `Kuantitas muatan ${this.isPickUp ? 'pengambilan' : 'pengiriman'}! <strong>${
                this.activity.delivery_order?.number
              }</strong> berhasil diubah!`,
            },
          ],
        },
      })

      if (this.adjustedDeliveryLoads.length > 0) {
        this.track('Delivery Load Adjustment after Transition', {
          'Fleet Order Number': this.fleetTaskDetail.number,
          'Activity Name': this.isPickUp ? 'Ambil' : 'Kirim',
        })
      }

      this.adjustedDeliveryLoads = []
      EventBus.$emit('refetch-sequence')
    },
  },
}
</script>

<style scoped lang="scss">
.label__do-number {
  color: #344264;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  margin: 0;
}

.mc-alert-container {
  .mc-alert {
    padding: 2px;
    .mc-alert-icon {
      &:before {
        font-size: 14px;
      }
    }
  }
}

.icon-circle-blue {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #007bff; /* Blue background color */
  margin-right: 8px;

  .mc-fa {
    color: white; /* Make the icon white for better contrast */
    &:before {
      font-size: 20px;
    }
  }
}

:deep(.note-textarea) {
  textarea {
    height: 75px;
    min-height: 75px;
    resize: vertical;
  }
}
</style>
